import { ReactNode } from 'react'
import { Box } from '@mui/material'

type PageHeaderProps = {
  /** The resource's name, not the route's name. Rendered as the page's h1. */
  title: string
  /** One line under the title, 12px ink-400. */
  subtitle?: string
  /** A status Chip, sitting beside the title. */
  status?: ReactNode
  /**
   * Every page-level action, right-aligned, at most one of them `contained`.
   * Nothing page-level belongs anywhere else on the page.
   */
  actions?: ReactNode
}

/**
 * The 46px top strip: page title, its status, and every page-level action.
 *
 * Replaces `Supplyvue-ui/PageHeader` (Typography h2 with a 24px bottom margin)
 * and, with the shell, the `Paper` crumb bar's 50/50 split. Sticky so the title
 * and its actions stay reachable once a long grid scrolls underneath.
 */
export const PageHeader = ({
  title,
  subtitle,
  status,
  actions,
}: PageHeaderProps) => (
  <Box
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 'var(--z-sticky)',
      minHeight: 'var(--top-strip-h)',
      flexShrink: 0,
      background: 'var(--surface-0)',
      borderBottom: '1px solid var(--line-300)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      px: '14px',
      py: '6px',
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
        }}
      >
        <Box
          component="h1"
          sx={{
            m: 0,
            fontFamily: 'var(--font-sans)',
            fontSize: 22,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            lineHeight: 1.15,
            color: 'var(--ink-900)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Box>
        {status}
      </Box>
      {subtitle && (
        <Box
          sx={{
            fontSize: 12,
            color: 'var(--ink-400)',
            lineHeight: 1.3,
            mt: '1px',
          }}
        >
          {subtitle}
        </Box>
      )}
    </Box>

    <Box sx={{ flex: 1 }} />

    {actions && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {actions}
      </Box>
    )}
  </Box>
)
